import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignQuoteSectionModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { updateCampaignQuoteKey } from '@redux/modules/campaignQuote'
import { loadDynamicCampaignQuoteItems } from '@redux/modules/campaignQuoteItem'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuoteSection/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuoteSection/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuoteSection/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuoteSection/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuoteSection/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuoteSection/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuoteSection/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuoteSection/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuoteSection/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuoteSection/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuoteSection/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuoteSection/DELETE_FAILURE'

export type CampaignQuoteSectionModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteSectionModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuoteSections(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuoteSections.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quote_sections.json', options)
      .then(({ data }: { data: CampaignQuoteSectionModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuoteSection(campaignQuoteSectionId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuoteSections.loadedForKeys]
    const loadedIds = [...state.campaignQuoteSections.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteSectionId && !loadedIds.includes(campaignQuoteSectionId)){
      loadedIds.push(campaignQuoteSectionId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quote_sections/${campaignQuoteSectionId}.json`, options)
      .then(({ data }: { data: CampaignQuoteSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteSection(
  campaignQuoteSection: Partial<CampaignQuoteSectionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_section: campaignQuoteSection,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_sections.json', options, config)
      .then(({ data }: { data: CampaignQuoteSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteSection(
  // @ts-expect-error TS2344
  campaignQuoteSection: UpdateParams<CampaignQuoteSectionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_section: campaignQuoteSection,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_sections/${campaignQuoteSection.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateQuoteSectionOrder(
  campaignQuoteId: number,
  sortedQuoteSectionIds: number[],
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_id: campaignQuoteId,
      sorted_ids: sortedQuoteSectionIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/campaign_quote_sections/update_sort_order.json', options, config)
      .then(({ data }: { data: CampaignQuoteSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// @ts-expect-error TS2344
export function deleteCampaignQuoteSection(campaignQuoteSection: DeleteParams<CampaignQuoteSectionModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quote_sections/${campaignQuoteSection.id}.json`, {}, config)
      .then(({ data }: { data: { campaign_quote_id: number, id: number, quote_total: number } }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        // Update Quote Total
        if (data.quote_total && data.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data.campaign_quote_id,
            key: 'total',
            value: data.quote_total,
          }))
        }

        if (data.campaign_quote_id){
          dispatch(loadDynamicCampaignQuoteItems({ campaign_quote_id: data.campaign_quote_id }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteSectionModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteSectionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
